// TraceRow — per-column renderers + per-row visual modifiers for the
// trace listing. The actual row chrome (grid template, hover, click,
// virtualization) lives in DataTable; this file only contributes the
// trace-specific parts: column schema, cell renderers, and the
// active/context className/opacity logic.

import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { sourceClientSummary, modelLabel } from "../lib/agentIdentity.js";
import { traceHarnessView } from "../lib/traceTooling.js";
import { useSessionMessageCount } from "../lib/sessionMessageCounts.js";
import { answerGapLabel, overdueAnswerCount } from "../lib/copilotAnswerGaps.js";
import HoverTooltip from "./HoverTooltip.jsx";
import TraceEvidenceButton from "./TraceEvidenceButton.jsx";

const DIM_SHORT = {
    session: "session",
    endpoint: "endpoint",
    files: "file",
    hosts: "host",
    skills: "skill",
    cluster: "topic",
    agent: "agent",
};

// Column schemas. The `request` column is `flex: true` so it stretches
// to fill the row; other columns take pixel widths. Width is still
// nominally set on flex columns for the column-state hooks but isn't
// used in the grid template.
export const TRACE_COLUMNS_3 = [
    { id: "time", label: "Time", width: 72 },
    { id: "request", label: "Request", width: 200, flex: true },
    { id: "model", label: "Model", width: 120 },
    { id: "harness", label: "Harness", width: 160 },
];

export const TRACE_COLUMNS_4 = [
    { id: "time", label: "Time", width: 72 },
    { id: "request", label: "Request", width: 200, flex: true },
    { id: "endpoint", label: "Endpoint", width: 80 },
    { id: "model", label: "Model", width: 120 },
    { id: "harness", label: "Harness", width: 160 },
];

// Session-grain columns: one row per conversation. `time` is the session's
// newest request; the `session` column carries that request's summary over a
// line of session facts (how many requests, since when).
export const SESSION_COLUMNS_3 = [
    { id: "time", label: "Latest", width: 72 },
    { id: "session", label: "Session", width: 200, flex: true },
    { id: "model", label: "Model", width: 120 },
    { id: "harness", label: "Harness", width: 160 },
];

export const SESSION_COLUMNS_4 = [
    { id: "time", label: "Latest", width: 72 },
    { id: "session", label: "Session", width: 200, flex: true },
    { id: "endpoint", label: "Endpoint", width: 80 },
    { id: "model", label: "Model", width: 120 },
    { id: "harness", label: "Harness", width: 160 },
];

// Unwrap a list entry into a uniform shape regardless of whether the
// list is anchor-only (entry === trace) or context-mode (entry holds
// `{ trace, isContext, score, matches }`).
export function unwrapTraceEntry(entry, contextMode) {
    if (contextMode) {
        return {
            trace: entry?.trace,
            isCtx: Boolean(entry?.isContext),
            score: entry?.score ?? 0,
            matches: entry?.matches,
        };
    }
    return { trace: entry, isCtx: false, score: 0, matches: undefined };
}

// Namespace → badge accent. A signal's annotation `type` is namespaced
// (risk.* / pii.* / audit.* / milestone.* / signal.*); colour the pill by that
// prefix so a risk reads hot, pii reads warm, and audit (normal agent activity,
// visibility-only) reads muted — not as a threat.
const SIGNAL_ACCENT = {
    risk: "var(--red,#c0392b)",
    pii: "var(--amber,#b8860b)",
    audit: "var(--muted)",
    milestone: "var(--green)",
    signal: "var(--green)",
};
function signalAccent(type) {
    const ns = typeof type === "string" ? type.split(".")[0] : "";
    return SIGNAL_ACCENT[ns] || "var(--green)";
}

// "What matched, and where" pulled from a fired signal's annotation `value`.
// Each signal authors its own value shape, so cover the common keys rather
// than coupling to any one signal: `match` (literal), `hits`/`classes`/
// `matches`/`host`/`provider` (per-signal summaries), and `where`/`tool`
// (the surface — user turn, a tool result, a tool call, a tool def).
export function signalDetail(value) {
    if (!value || typeof value !== "object") return { match: null, where: null };
    let match = null;
    if (typeof value.match === "string" && value.match) match = value.match;
    else if (Array.isArray(value.hits) && value.hits.length) match = value.hits.map((h) => h?.kind || h?.match).filter(Boolean).join(", ");
    else if (value.classes && typeof value.classes === "object") match = Object.entries(value.classes).map(([k, n]) => `${k}×${n}`).join(", ");
    else if (Array.isArray(value.matches) && value.matches.length) match = value.matches.join(", ");
    else if (typeof value.host === "string" && value.host) match = value.host;
    else if (typeof value.provider === "string" && value.provider) match = value.provider;
    const where = typeof value.where === "string" && value.where
        ? value.where
        : (typeof value.tool === "string" && value.tool ? `tool · ${value.tool}` : null);
    return { match, where };
}

export const COPILOT_ANSWER_STATUS_LABELS = Object.freeze({
    captured: "Answer captured",
    not_yet_retrieved: "Waiting for answer",
    no_answer_returned: "No answer captured",
});

export function AnswerGapMarker({ prompts, now = Date.now() }) {
    const count = overdueAnswerCount(prompts, now);
    if (count === 0) return null;
    return (
        <span
            data-answer-gap={count}
            className="w-fit rounded-[2px] bg-[color:var(--control-bg)] px-[3px] py-[0.5px] text-[8px] leading-[1.2] tracking-[0.04em] uppercase text-[color:var(--muted)]"
            title="Copilot prompts with no captured answer after 15 minutes"
        >
            {answerGapLabel(count)}
        </span>
    );
}

// The states of a prompt-body cell, shared by the request and session columns:
//   1. summary populated → render the truncated text
//   1a. trace.summaryMissing -> a session listed from its capture frames that
//      the prompt index holds no prompt of (older than it, or never analyzed):
//      there is no summary to load or wait for.
//   2. trace.summaryUnavailable → redacted placeholder. The list query
//      deliberately skipped the body (caller lacks the sensitive-prompt read,
//      or the body is too large to inline); detail panel loads it on click.
//   3. trace.filtered → skwach decoded the captured frame to no user prompt
//      (e.g. a Robin metadata/end-marker frame). It will never enrich — label
//      it, don't show "Pending".
//   4. summary empty AND not flagged/filtered → "Pending" badge (skwach hasn't
//      analyzed this prompt yet).
function summaryStateNode(trace) {
    if (trace.summary) {
        return (
            <strong className="truncate text-[11px] leading-[1.08] font-medium">
                {trace.summary}
            </strong>
        );
    }
    if (trace.summaryMissing) {
        return (
            <span className="inline-flex items-center gap-[6px] text-[11px] leading-[1.08] text-[color:var(--muted-soft)]">
                No summary
            </span>
        );
    }
    if (trace.summaryUnavailable) {
        return (
            <span
                className="inline-flex items-center gap-[6px] text-[11px] leading-[1.08] text-[color:var(--muted-soft)]"
                title="Open this trace to load the prompt body"
            >
                Prompt body hidden. Click to view.
            </span>
        );
    }
    if (trace.filtered) {
        return (
            <span
                className="inline-flex items-center gap-[6px] text-[11px] leading-[1.08] text-[color:var(--muted-soft)]"
                title="This captured frame contains no user prompt, such as a Gemini Desktop metadata frame"
            >
                Non-prompt frame
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-[6px] text-[11px] leading-[1.08] text-[color:var(--muted-soft)]"
            title="The summarizer has not processed this prompt yet"
        >
            <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[color:var(--muted-soft)] animate-pulse" />
            Pending
        </span>
    );
}

// The session's shape under the newest request's summary: how many messages
// its record holds and when the first request in the window was. The count is
// read while the row is on screen (sessionMessageCounts.js); until it lands
// the line says so, and a count that could not be read is left out rather
// than guessed. "since" is the first request IN THE WINDOW, not the
// conversation's birth, so the line stays honest about the range.
function SessionFacts({ trace }) {
    const { count, loading } = useSessionMessageCount(trace.sessionId || null, trace.firstTimestamp ?? null);
    const first = trace.firstTimestamp;
    const since = Number.isFinite(first) ? `since ${dayLabelFormatter.format(first)} ${timeLabelFormatter.format(first)}` : "";
    const messages = loading
        ? "counting messages"
        : Number.isFinite(count) ? `${count.toLocaleString()} ${count === 1 ? "message" : "messages"}` : "";
    return (
        <span data-session-facts="" className="truncate text-[9px] leading-[1.2] text-[color:var(--muted-soft)]">
            {[messages, since].filter(Boolean).join(" · ")}
        </span>
    );
}

export function renderTraceCell(trace, col, { isCtx, matches, signals }) {
    switch (col.id) {
        case "time":
            return (
                <div className="grid gap-0">
                    <strong className="text-[10px] leading-[1.12] font-medium">
                        {timeLabelFormatter.format(trace.timestamp)}
                    </strong>
                    <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">
                        {dayLabelFormatter.format(trace.timestamp)}
                    </span>
                </div>
            );
        case "session":
            return (
                <div className="grid min-w-0 gap-[2px]">
                    <div className="flex min-w-0 items-center gap-[6px]">
                        {summaryStateNode(trace)}
                    </div>
                    <AnswerGapMarker prompts={trace.unansweredPrompts} />
                    <SessionFacts trace={trace} />
                </div>
            );
        case "request": {
            return (
                <div className="grid min-w-0 gap-[2px]">
                    <div className="flex min-w-0 items-center gap-[6px]">
                        {summaryStateNode(trace)}
                    </div>
                    <AnswerGapMarker prompts={trace.unansweredPrompts} />
                    {isCtx && matches?.length > 0 && (
                        <div className="flex gap-[3px] pl-[12px]">
                            {matches.map((dimId) => (
                                <span
                                    key={dimId}
                                    className="rounded-[2px] bg-[color:var(--control-bg)] px-[3px] py-[0.5px] text-[8px] leading-[1.2] tracking-[0.04em] uppercase text-[color:var(--muted)]"
                                >
                                    {DIM_SHORT[dimId] || dimId}
                                </span>
                            ))}
                        </div>
                    )}
                    {signals?.length > 0 && (
                        <div className="flex flex-wrap gap-[3px] pt-[3px]">
                            {signals.map((s) => {
                                const det = signalDetail(s.value);
                                const conf = typeof s.confidence === "number" ? ` · ${Math.round(s.confidence * 100)}%` : "";
                                const label = (
                                    <div className="grid gap-[2px] text-left normal-case tracking-normal">
                                        <span style={{ color: signalAccent(s.type) }}>{s.type}{conf}</span>
                                        {det.match ? <span className="text-[color:var(--muted)]">matched: {det.match}</span> : null}
                                        {det.where ? <span className="text-[color:var(--muted-soft)]">in: {det.where}</span> : null}
                                    </div>
                                );
                                return (
                                    <HoverTooltip key={s.command} label={label} placement="top" wide>
                                        <span
                                            className="inline-flex items-center gap-[3px] rounded-[2px] border px-[3px] py-[0.5px] text-[8px] leading-[1.2] tracking-[0.04em] uppercase"
                                            style={{ borderColor: signalAccent(s.type), color: signalAccent(s.type) }}
                                        >
                                            <span className="h-[4px] w-[4px] rounded-full" style={{ background: signalAccent(s.type) }} />
                                            {s.command}
                                        </span>
                                    </HoverTooltip>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }
        case "endpoint":
            return (
                <strong className="truncate text-[11px] leading-[1.08] font-medium text-[color:var(--muted)]">
                    {trace.endpointName}
                </strong>
            );
        case "model": {
            const attributed = sourceClientSummary(trace.clientAttributions, { truncated: trace.clientAttributionsTruncated });
            return (
                <strong className="truncate text-[11px] leading-[1.08] font-medium">
                    {modelLabel(trace.reportedModel ?? trace.model, trace.provider, Boolean(attributed))}
                </strong>
            );
        }
        case "harness": {
            const { label, tooling } = traceHarnessView(trace);
            return <TraceEvidenceButton tooling={tooling} hover label={label} />;
        }
        default:
            return null;
    }
}

// Per-row className: active gets the green-soft + inset stripe, context
// rows get a muted background and color treatment. Active wins over
// context (a selected context trace reads as anchor while focused).
export function traceRowClassName({ active, isCtx }) {
    if (active) {
        return "!bg-[color:var(--green-soft)] shadow-[inset_2px_0_0_var(--green)]";
    }
    if (isCtx) {
        return "bg-[color:var(--control-bg)] text-[color:var(--muted)]";
    }
    return "";
}

// Per-row inline style: context rows fade with a score-modulated
// opacity so the strongest fuzzy matches read most strongly. Active
// rows always render at full opacity regardless of score.
export function traceRowStyle({ active, isCtx, score }) {
    if (!isCtx || active) return null;
    const strength = Math.min((score || 0) / 80, 1);
    return { opacity: 0.4 + strength * 0.6 };
}
